import { useTranslation } from "react-i18next";
import _ from "lodash";
import { ApolloError, useQuery, useSubscription } from "@apollo/react-hooks";
import { DocumentNode } from "graphql";
import { TFunction } from "i18next";
import { MY_HR_EMPLOYEE_SUBSCRIPTION, MY_HR_GET_USERS } from "my-hr/graphql";
import { useOcCurrentUserState, useOcPolling } from "oc/hooks";

export interface UseMyHrUsersParams {
  filterField?: string;
  filterSelected?: string[];
  page?: number;
  pageSize?: number;
  searchField?: string;
  searchText: string;
  sortDirection?: string;
  sortField?: string;
}

export interface User {
  employee: string;
  validTo?: string;
  lastLogin?: string;
  active?: boolean;
  code?: string;
  [key: string]: any;
}

export interface UsersList {
  page: number;
  pageSize: number;
  rows: User[];
  total: number;
  totalPages: number;
}

interface GetUsersData {
  users: UsersList;
}

interface GetUsersVars {
  filterField?: string;
  filterSelected?: string[];
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: string;
  searchField?: string;
  searchText?: string;
}

export interface UseMyHrUsersResult {
  data: UsersList;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * Hook to fetch and subscribe to the HR users list with filtering, paging, and polling.
 */
export function useMyHrUsers({
  filterField,
  filterSelected,
  page,
  pageSize,
  searchField,
  searchText,
  sortDirection,
  sortField,
}: UseMyHrUsersParams): UseMyHrUsersResult {
  const { t, i18n }: { t: TFunction; i18n: any } = useTranslation();
  const { contractCode } = useOcCurrentUserState();

  // Only search when text length > 2
  const innerSearchField =
    searchField && searchText.length > 2 ? searchField : "";
  const innerSearchText = searchText.length > 2 ? searchText : "";

  const { data, loading, error, refetch } = useQuery<
    GetUsersData,
    GetUsersVars
  >(MY_HR_GET_USERS as DocumentNode, {
    fetchPolicy: "cache-and-network",
    pollInterval: useOcPolling(1000 * 60),
    skip: !contractCode,
    variables: {
      filterField,
      filterSelected,
      page,
      pageSize,
      sortField,
      sortDirection,
      searchField: innerSearchField,
      searchText: innerSearchText,
    },
  });

  useSubscription(MY_HR_EMPLOYEE_SUBSCRIPTION as DocumentNode, {
    fetchPolicy: "network-only",
    onData: () => {
      refetch();
    },
  });

  // Provide a default structure if data is not yet available
  const users: UsersList = _.cloneDeep(data?.users) ?? {
    page: page ?? 1,
    pageSize: pageSize ?? 25,
    rows: [],
    total: 0,
    totalPages: 1,
  };

  // Enrich each user row
  users.rows = users.rows.map((item) => {
    const now = Date.now();
    const validToTime = item.validTo
      ? new Date(item.validTo).getTime()
      : Infinity;
    return {
      ...item,
      active: validToTime >= now,
      lastLogin: item.lastLogin
        ? new Date(item.lastLogin).toLocaleString(i18n.language, {
          hour12: false,
        })
        : t("MY_HR_NOT_LOGGED_IN"),
      code: item.employee,
    };
  });

  const refetchVoid = () => {
    void refetch();
  };

  return { data: users, loading, error, refetch: refetchVoid };
}

export default useMyHrUsers;
